/**
 * Preview of a .txt / .md / .html document's text, fetched through
 * GET /api/documents/:id/preview.
 *
 * Rendered Markdown and HTML go into a sandboxed iframe (no scripts, opaque
 * origin, no network via its own CSP), so document markup can't touch the app
 * or load remote resources. Plain text and "Source" view use a <pre>.
 */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { marked } from "marked";
import { Button } from "@/components/ui/button";
import { documentApi, type DocumentPreviewKind } from "@/lib/proxy-api";
import { ApiException } from "@/lib/api-client";

const PREVIEWABLE: Record<string, DocumentPreviewKind> = {
  txt: "text",
  text: "text",
  md: "markdown",
  markdown: "markdown",
  html: "html",
  htm: "html",
};

export function previewKindForFilename(filename: string): DocumentPreviewKind | null {
  const base = filename.split(/[/\\]/).pop() ?? "";
  const dot = base.lastIndexOf(".");
  if (dot <= 0) return null;
  return PREVIEWABLE[base.slice(dot + 1).toLowerCase()] ?? null;
}

const FRAME_STYLES = `
  body { font: 14px/1.6 system-ui, -apple-system, "Segoe UI", sans-serif; color: #1f2328; background: #fff; margin: 16px; overflow-wrap: anywhere; }
  h1, h2, h3, h4 { line-height: 1.25; margin: 1.2em 0 0.5em; }
  h1 { font-size: 1.6em; } h2 { font-size: 1.35em; } h3 { font-size: 1.15em; }
  pre { background: #f6f8fa; padding: 12px; overflow: auto; white-space: pre-wrap; }
  code { font: 12.5px ui-monospace, SFMono-Regular, Menlo, monospace; background: #f6f8fa; padding: 0 3px; }
  pre code { padding: 0; }
  table { border-collapse: collapse; } th, td { border: 1px solid #d0d7de; padding: 4px 8px; text-align: left; }
  blockquote { margin: 0; padding-left: 12px; border-left: 3px solid #d0d7de; color: #57606a; }
  img { max-width: 100%; }
`;

function frameDocument(bodyHtml: string): string {
  // The meta CSP blocks every network load from inside the frame (images, fonts,
  // stylesheets, forms); inline styles are allowed so documents keep their look.
  return `<!doctype html><html><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; img-src data:; form-action 'none'">
<style>${FRAME_STYLES}</style></head><body>${bodyHtml}</body></html>`;
}

export function DocumentPreview({
  documentId,
  filename,
  tenantId,
}: {
  documentId: string;
  filename: string;
  tenantId?: string;
}) {
  const expectedKind = previewKindForFilename(filename);
  const [view, setView] = useState<"rendered" | "source">("rendered");

  const q = useQuery({
    queryKey: ["document-preview", tenantId, documentId],
    queryFn: () => documentApi.preview(documentId, { tenantId }),
    enabled: expectedKind !== null,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const preview = q.data;

  const frameHtml = useMemo(() => {
    if (!preview || preview.kind === "text") return "";
    const body = preview.kind === "markdown"
      ? (marked.parse(preview.content, { async: false, gfm: true }) as string)
      : preview.content;
    return frameDocument(body);
  }, [preview]);

  if (!expectedKind) {
    return (
      <div className="rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground">
        Preview is available for .txt, .md and .html documents.
      </div>
    );
  }
  if (q.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading preview…</p>;
  }
  if (q.error || !preview) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
        {q.error instanceof ApiException ? q.error.message : "The preview could not be loaded."}
      </div>
    );
  }

  const renderable = preview.kind !== "text";
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          {preview.kind === "markdown" ? "Markdown" : preview.kind === "html" ? "HTML" : "Plain text"}
          {preview.content_length !== null && ` · ${formatBytes(preview.content_length)}`}
          {renderable && view === "rendered" && " · scripts, links and remote images are disabled"}
        </p>
        {renderable && (
          <div className="flex gap-1" role="group" aria-label="Preview view">
            <Button
              size="sm"
              variant={view === "rendered" ? "secondary" : "ghost"}
              aria-pressed={view === "rendered"}
              onClick={() => setView("rendered")}
            >
              Rendered
            </Button>
            <Button
              size="sm"
              variant={view === "source" ? "secondary" : "ghost"}
              aria-pressed={view === "source"}
              onClick={() => setView("source")}
            >
              Source
            </Button>
          </div>
        )}
      </div>

      {preview.truncated && (
        <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          Showing the first 1 MB of this document.
        </div>
      )}

      {renderable && view === "rendered" ? (
        <iframe
          title={`Preview of ${preview.filename}`}
          sandbox=""
          referrerPolicy="no-referrer"
          srcDoc={frameHtml}
          className="h-[28rem] w-full rounded-md border bg-white"
        />
      ) : preview.content.length === 0 ? (
        <div className="rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground">This document is empty.</div>
      ) : (
        <pre className="max-h-[28rem] overflow-auto rounded-md border bg-muted/40 p-3 text-sm whitespace-pre-wrap">
          {preview.content}
        </pre>
      )}
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
